import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { TextButton } from '@gonfalon/launchpad-experimental/Button';
import { toCreateMetric, toCreateMetricGroup } from '@gonfalon/navigator/navigators';
import type { Path } from '@gonfalon/router/isPath';
import { Button, Link, LinkButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './MetricsSearch.module.css';

interface MetricsSearchEmptyStateProps {
  category: string;
  searchValue: string;
  projectKey: string;
  environmentKey: string;
  disableCreate?: boolean;
  onCategoryChange: (category: string) => void;
  onCreateMetric?: () => void;
  onCreateMetricGroup?: () => void;
}

// Helper function to render create button/link
const renderCreateAction = (
  text: string,
  path: Path,
  onPress?: () => void,
  disabled?: boolean,
  showExternalIcon = true,
) => {
  if (disabled) {
    return null;
  }

  if (onPress) {
    return <Button onPress={onPress}>{text}</Button>;
  }

  return (
    <LinkButton href={path} target="_blank">
      {text} {showExternalIcon && <Icon name="link-external" />}
    </LinkButton>
  );
};

export function MetricsSearchEmptyState({
  category,
  searchValue,
  projectKey,
  environmentKey,
  disableCreate,
  onCategoryChange,
  onCreateMetric,
  onCreateMetricGroup,
}: MetricsSearchEmptyStateProps) {
  const hasSearchQuery = searchValue && searchValue.trim().length > 0;

  // Currently selected category - no search logic needed
  if (category === 'currently-selected') {
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics or metric groups selected</h3>
        <p className={styles.emptyStateDescription}>
          Select metrics or metric groups from other categories to see them here
        </p>
      </div>
    );
  }

  // Metric groups category
  if (category === 'metric-groups' || category === 'funnel-groups') {
    if (!hasSearchQuery) {
      // No search query - show create action
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>This project doesn't have any metric groups.</h3>
          <p className={styles.emptyStateDescription}>Use metric groups to organize metrics and run experiments</p>
          {renderCreateAction(
            'Create metric group',
            toCreateMetricGroup({ projectKey, environmentKey }),
            onCreateMetricGroup,
            disableCreate,
          )}
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metric groups found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            All Metrics
          </TextButton>
        </p>
      </div>
    );
  }

  // Autogenerated category
  if (category === 'autogenerated') {
    if (!hasSearchQuery) {
      // No search query - show autogenerated-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>This project doesn't have any autogenerated metrics.</h3>
          <p className={styles.emptyStateDescription}>
            LaunchDarkly automatically generates metrics from select metric event sources.{' '}
            <Link href={getDocumentationUrl('/home/metrics/autogen-metrics')} target="_blank" rel="noopener noreferrer">
              Learn more
            </Link>
          </p>
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            All Metrics
          </TextButton>
        </p>
      </div>
    );
  }

  // Warehouse native category
  if (category === 'warehouse-native') {
    if (!hasSearchQuery) {
      // No search query - show warehouse native-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>This project doesn't have any warehouse native metrics.</h3>
          <p className={styles.emptyStateDescription}>
            LaunchDarkly can ingest metric events from your external data warehouse.{' '}
            <Link
              href={getDocumentationUrl('/home/warehouse-native/metrics')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </Link>
          </p>
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            All Metrics
          </TextButton>
        </p>
      </div>
    );
  }

  // User-defined category
  if (category === 'user-defined') {
    if (!hasSearchQuery) {
      // No search query - show user-defined-specific empty state
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>This project has no user-defined metrics.</h3>
          <p className={styles.emptyStateDescription}>
            Use metrics to measure audience
            <br />
            behaviors affected by flags and experiments
          </p>
          {renderCreateAction(
            'Create metric',
            toCreateMetric({ projectKey, environmentKey }),
            onCreateMetric,
            disableCreate,
          )}
        </div>
      );
    }

    // Has search query - show category switching suggestion
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            All Metrics
          </TextButton>
        </p>
      </div>
    );
  }

  // Release guardrails category
  if (category === 'release-guardrails') {
    if (!hasSearchQuery) {
      return (
        <div className={styles.emptyState}>
          <h3 className={styles.emptyStateTitle}>No release guardrail metrics found.</h3>
          <p className={styles.emptyStateDescription}>
            Metrics added to a release policy get automatically added to all guarded rollouts across environments
          </p>
        </div>
      );
    }

    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
            All Metrics
          </TextButton>
        </p>
      </div>
    );
  }

  // All metrics category or other metric categories
  const isAllMetricsCategory = category === 'all';

  if (!hasSearchQuery) {
    // No search query - show default empty state
    const title = "This project doesn't have any metrics.";
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>{title}</h3>
        <p className={styles.emptyStateDescription}>
          Use metrics to measure audience
          <br />
          behaviors affected by flags and experiments
        </p>
        {renderCreateAction(
          'Create metric',
          toCreateMetric({ projectKey, environmentKey }),
          onCreateMetric,
          disableCreate,
        )}
      </div>
    );
  }

  // Has search query
  if (isAllMetricsCategory) {
    return (
      <div className={styles.emptyState}>
        <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
        <p className={styles.emptyStateDescription}>
          Try searching in{' '}
          <TextButton className={styles.textButton} onPress={() => onCategoryChange('metric-groups')}>
            Metric Groups
          </TextButton>
        </p>
      </div>
    );
  }

  // Other categories (most-used, recently-added) with search query
  return (
    <div className={styles.emptyState}>
      <h3 className={styles.emptyStateTitle}>No metrics found.</h3>
      <p className={styles.emptyStateDescription}>
        Try searching in{' '}
        <TextButton className={styles.textButton} onPress={() => onCategoryChange('all')}>
          All Metrics
        </TextButton>{' '}
        or{' '}
        <TextButton className={styles.textButton} onPress={() => onCategoryChange('metric-groups')}>
          Metric Groups
        </TextButton>
      </p>
    </div>
  );
}
